import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export const HELP_LIMITS = {
  maxScreenshots: 5,
  maxScreenshotBytes: 10 * 1024 * 1024,
  /** Backend allows 5000; the rest is reserved for the technical-details footer. */
  maxDescription: 4400,
  acceptedTypes: ['image/png', 'image/jpeg', 'image/webp'],
} as const;

const EXTENSION_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

export interface HelpSubmission {
  name: string;
  email: string;
  description: string;
  screenshots: File[];
  patientName?: string;
  /** yyyy-MM-dd */
  visitDate?: string;
  visitId?: string;
  visitDetails?: string;
}

export interface HelpResponse {
  success: boolean;
  message: string;
  referenceId: string;
}

export interface ImageValidationResult {
  accepted: File[];
  errors: string[];
}

export interface TechnicalContext {
  routeTemplate: string;
  userId: number | null;
  submittedAt: Date;
}

@Injectable({ providedIn: 'root' })
export class HelpService {
  private readonly http = inject(HttpClient);

  /** Posts to the existing `POST /api/help` (multipart). Auth headers come from the auth interceptor. */
  submit(submission: HelpSubmission): Observable<HelpResponse> {
    return this.http.post<HelpResponse>(`${environment.apiUrl}/help`, buildFormData(submission));
  }
}

/** Only fields the backend accepts are sent; empty optional fields are omitted. */
export function buildFormData(s: HelpSubmission): FormData {
  const form = new FormData();
  form.append('name', s.name.trim());
  form.append('email', s.email.trim());
  form.append('description', s.description.trim());
  appendIfPresent(form, 'patientName', s.patientName);
  appendIfPresent(form, 'visitDate', s.visitDate);
  appendIfPresent(form, 'visitId', s.visitId);
  appendIfPresent(form, 'visitDetails', s.visitDetails);
  for (const file of s.screenshots) {
    form.append('screenshots', file, file.name);
  }
  return form;
}

function appendIfPresent(form: FormData, key: string, value: string | undefined): void {
  const v = value?.trim();
  if (v) {
    form.append(key, v);
  }
}

/** Declared type, or the extension when the browser reports none (some mobile pickers). */
export function imageTypeOf(file: File): string | null {
  const declared = file.type.toLowerCase();
  if ((HELP_LIMITS.acceptedTypes as readonly string[]).includes(declared)) {
    return declared;
  }
  if (!declared) {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return EXTENSION_TYPES[ext] ?? null;
  }
  return null;
}

/**
 * Checks count, type and size. The backend re-validates the bytes; this is for fast, friendly feedback.
 */
export function validateImages(existingCount: number, candidates: File[]): ImageValidationResult {
  const accepted: File[] = [];
  const errors: string[] = [];
  const maxMb = HELP_LIMITS.maxScreenshotBytes / (1024 * 1024);
  for (const file of candidates) {
    if (imageTypeOf(file) === null) {
      errors.push(`"${file.name || 'This file'}" is not a PNG, JPEG or WebP image.`);
    } else if (file.size > HELP_LIMITS.maxScreenshotBytes) {
      errors.push(`"${file.name || 'This image'}" is larger than ${maxMb} MB.`);
    } else if (file.size === 0) {
      errors.push(`"${file.name || 'This image'}" is empty.`);
    } else if (existingCount + accepted.length >= HELP_LIMITS.maxScreenshots) {
      errors.push(`You can attach up to ${HELP_LIMITS.maxScreenshots} screenshots.`);
      break;
    } else {
      accepted.push(file);
    }
  }
  return { accepted, errors };
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** User-facing message for a failed submission; never includes request content. */
export function friendlyError(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    switch (err.status) {
      case 0:
        return "We couldn't reach the server. Check your connection and try again. Your request is still here.";
      case 400: {
        const fieldErrors = err.error?.errors as Record<string, string> | undefined;
        const first = fieldErrors ? Object.values(fieldErrors)[0] : undefined;
        return first || err.error?.message || 'Some details need another look. Please review the form and try again.';
      }
      case 413:
        return `One of your screenshots is too large. Each image must be under ${HELP_LIMITS.maxScreenshotBytes / (1024 * 1024)} MB.`;
      case 429:
        return "You've sent several requests recently. Please wait a little while and try again.";
      case 403:
        return "Your account can't send support requests right now. Please contact your administrator.";
      default:
        break;
    }
  }
  return 'Something went wrong sending your request. Your details are still here, so please try again.';
}

/** `Chrome 126`, `Safari 17`, ... from the user agent. */
export function browserOf(ua: string): string {
  const rules: [RegExp, string][] = [
    [/Edg(?:e|A|iOS)?\/(\d+)/, 'Edge'],
    [/OPR\/(\d+)/, 'Opera'],
    [/(?:Firefox|FxiOS)\/(\d+)/, 'Firefox'],
    [/(?:Chrome|CriOS)\/(\d+)/, 'Chrome'],
    [/Version\/(\d+).*Safari/, 'Safari'],
  ];
  for (const [re, name] of rules) {
    const m = ua.match(re);
    if (m) {
      return `${name} ${m[1]}`;
    }
  }
  return 'Unknown browser';
}

export function osOf(ua: string): string {
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS';
  if (/CrOS/.test(ua)) return 'ChromeOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown OS';
}

export function deviceOf(ua: string, viewportWidth: number): 'mobile' | 'tablet' | 'desktop' {
  if (/iPad|Tablet/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) return 'tablet';
  if (/Mobi|iPhone|iPod|Android/.test(ua)) return 'mobile';
  // iPadOS reports a Mac user agent; touch + a tablet-sized viewport identifies it.
  if (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) {
    return viewportWidth >= 768 ? 'tablet' : 'mobile';
  }
  return 'desktop';
}

/**
 * Technical details appended to the description (the backend has no separate field for them yet).
 * Deliberately excludes patient/visit identifiers, names, tokens and query strings: this text is forwarded to
 * Slack and email, which are not approved for PHI.
 */
export function technicalDetailsFooter(ctx: TechnicalContext, ua: string, viewportWidth: number): string {
  return [
    '',
    '',
    '--- Technical details ---',
    `Page: ${ctx.routeTemplate}`,
    `App version: ${environment.version}`,
    `Browser: ${browserOf(ua)}`,
    `OS: ${osOf(ua)}`,
    `Device: ${deviceOf(ua, viewportWidth)}`,
    `User ID: ${ctx.userId ?? 'unknown'}`,
    `Submitted: ${ctx.submittedAt.toISOString()}`,
  ].join('\n');
}

/** `userId` claim of the stored JWT (internal id, not PHI), or null. */
export function userIdFromToken(token: string | null): number | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload)) as { userId?: unknown };
    return typeof decoded.userId === 'number' ? decoded.userId : null;
  } catch {
    return null;
  }
}
